// Payment Error Handler Utility
// Maps various payment error codes to user-friendly messages with actionable guidance

export interface PaymentError {
  title: string;
  message: string;
  action: string;
  severity: 'error' | 'warning' | 'info';
  canRetry: boolean;
  contactSupport: boolean;
}

// Stripe decline code mappings
const STRIPE_DECLINE_CODES: Record<string, PaymentError> = {
  // Card data errors
  incorrect_number: {
    title: 'Invalid Card Number',
    message: 'The card number you entered is incorrect.',
    action: 'Please double-check your card number and try again.',
    severity: 'error',
    canRetry: true,
    contactSupport: false,
  },
  incorrect_cvc: {
    title: 'Invalid Security Code',
    message: 'The CVC/CVV code you entered is incorrect.',
    action: 'Please check the 3-4 digit security code on the back of your card and try again.',
    severity: 'error',
    canRetry: true,
    contactSupport: false,
  },
  incorrect_zip: {
    title: 'Invalid Postal Code',
    message: 'The postal code you entered doesn\'t match your card\'s billing address.',
    action: 'Please enter the postal code associated with your card\'s billing address.',
    severity: 'error',
    canRetry: true,
    contactSupport: false,
  },
  expired_card: {
    title: 'Card Expired',
    message: 'Your card has expired.',
    action: 'Please use a different card or contact your bank for a replacement.',
    severity: 'error',
    canRetry: false,
    contactSupport: false,
  },
  
  // Insufficient funds
  insufficient_funds: {
    title: 'Insufficient Funds',
    message: 'Your card doesn\'t have enough available funds for this purchase.',
    action: 'Please try a different payment method or add funds to your account.',
    severity: 'warning',
    canRetry: false,
    contactSupport: false,
  },
  card_velocity_exceeded: {
    title: 'Transaction Limit Exceeded',
    message: 'You\'ve exceeded your card\'s transaction limit.',
    action: 'Please wait 24 hours and try again, or use a different payment method.',
    severity: 'warning',
    canRetry: false,
    contactSupport: false,
  },
  withdrawal_count_limit_exceeded: {
    title: 'Daily Limit Exceeded',
    message: 'You\'ve reached your daily transaction limit.',
    action: 'Please try again tomorrow or use a different payment method.',
    severity: 'warning',
    canRetry: false,
    contactSupport: false,
  },
  
  // Authentication and security
  authentication_required: {
    title: 'Authentication Required',
    message: 'Your bank requires additional verification for this payment.',
    action: 'Please complete the authentication process or try a different card.',
    severity: 'info',
    canRetry: true,
    contactSupport: false,
  },
  fraudulent: {
    title: 'Transaction Blocked',
    message: 'This transaction was flagged by our fraud prevention system.',
    action: 'Please try a different payment method or contact support.',
    severity: 'error',
    canRetry: false,
    contactSupport: true,
  },
  
  // Card/account issues
  card_not_supported: {
    title: 'Card Not Supported',
    message: 'This card type is not supported for this purchase.',
    action: 'Please use a different card (Visa, Mastercard, or American Express).',
    severity: 'error',
    canRetry: false,
    contactSupport: false,
  },
  currency_not_supported: {
    title: 'Currency Not Supported',
    message: 'Your card doesn\'t support payments in this currency.',
    action: 'Please try a different card or contact your bank.',
    severity: 'error',
    canRetry: false,
    contactSupport: false,
  },
  lost_card: {
    title: 'Card Issue',
    message: 'There\'s an issue with your card.',
    action: 'Please contact your bank or use a different payment method.',
    severity: 'error',
    canRetry: false,
    contactSupport: false,
  },
  stolen_card: {
    title: 'Card Issue',
    message: 'There\'s an issue with your card.',
    action: 'Please contact your bank or use a different payment method.',
    severity: 'error',
    canRetry: false,
    contactSupport: false,
  },
  
  // Bank/issuer issues
  call_issuer: {
    title: 'Contact Your Bank',
    message: 'Your bank has declined this transaction.',
    action: 'Please contact your bank to authorize this payment and try again.',
    severity: 'warning',
    canRetry: true,
    contactSupport: false,
  },
  do_not_honor: {
    title: 'Payment Declined',
    message: 'Your bank has declined this payment.',
    action: 'Please contact your bank for more information or try a different card.',
    severity: 'warning',
    canRetry: false,
    contactSupport: false,
  },
  issuer_not_available: {
    title: 'Bank Unavailable',
    message: 'Your bank\'s payment system is temporarily unavailable.',
    action: 'Please try again in a few minutes or use a different card.',
    severity: 'warning',
    canRetry: true,
    contactSupport: false,
  },
  processing_error: {
    title: 'Processing Error',
    message: 'There was a temporary issue processing your payment.',
    action: 'Please try again in a few minutes.',
    severity: 'warning',
    canRetry: true,
    contactSupport: false,
  },
  
  // Generic fallback
  generic_decline: {
    title: 'Payment Declined',
    message: 'Your payment was declined by your bank.',
    action: 'Please contact your bank for more information or try a different payment method.',
    severity: 'warning',
    canRetry: false,
    contactSupport: false,
  },
};

// API error mappings
const API_ERROR_CODES: Record<string, PaymentError> = {
  subscription_not_found: {
    title: 'Subscription Not Found',
    message: 'We couldn\'t find your subscription.',
    action: 'Please refresh the page and try again.',
    severity: 'error',
    canRetry: true,
    contactSupport: true,
  },
  invalid_subscription_plan: {
    title: 'Invalid Plan',
    message: 'The selected subscription plan is not valid.',
    action: 'Please select a different plan and try again.',
    severity: 'error',
    canRetry: false,
    contactSupport: true,
  },
  promotion_code_invalid: {
    title: 'Invalid Promotion Code',
    message: 'The promotion code you entered is invalid or has expired.',
    action: 'Please check the code or remove it to continue.',
    severity: 'warning',
    canRetry: true,
    contactSupport: false,
  },
  promotion_code_already_used: {
    title: 'Promotion Code Not Available',
    message: 'This promotion code cannot be used because you have prior transactions.',
    action: 'Please remove the promotion code to continue with your payment.',
    severity: 'warning',
    canRetry: true,
    contactSupport: false,
  },
  currency_mismatch: {
    title: 'Currency Mismatch',
    message: 'You have an existing subscription with a different currency. Please use the same currency as your previous subscription.',
    action: 'Please change the currency to match your existing subscription and try again.',
    severity: 'warning',
    canRetry: true,
    contactSupport: false,
  },
  network_error: {
    title: 'Connection Error',
    message: 'We couldn\'t connect to our payment processor.',
    action: 'Please check your internet connection and try again.',
    severity: 'warning',
    canRetry: true,
    contactSupport: false,
  },
  server_error: {
    title: 'Server Error',
    message: 'Our servers are experiencing issues.',
    action: 'Please try again in a few minutes.',
    severity: 'error',
    canRetry: true,
    contactSupport: true,
  },
  rate_limit: {
    title: 'Too Many Attempts',
    message: 'You\'ve made too many payment attempts.',
    action: 'Please wait a few minutes before trying again.',
    severity: 'warning',
    canRetry: true,
    contactSupport: false,
  },
};

// Helper function to extract error information from various error sources
export function extractErrorInfo(error: any): PaymentError {
  // Check for Stripe decline code
  if (error.decline_code) {
    return STRIPE_DECLINE_CODES[error.decline_code] || STRIPE_DECLINE_CODES.generic_decline;
  }
  
  // Check for Stripe error code
  if (error.code) {
    return STRIPE_DECLINE_CODES[error.code] || API_ERROR_CODES[error.code] || getGenericError();
  }
  
  // Check API response error
  if (error.response?.data?.error) {
    const errorData = error.response.data.error;
    if (typeof errorData === 'string') {
      return mapErrorMessageToCode(errorData);
    }
    if (errorData.decline_code) {
      return STRIPE_DECLINE_CODES[errorData.decline_code] || STRIPE_DECLINE_CODES.generic_decline;
    }
    if (errorData.code) {
      return STRIPE_DECLINE_CODES[errorData.code] || API_ERROR_CODES[errorData.code] || getGenericError();
    }
  }
  
  // Check for network errors
  if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
    return API_ERROR_CODES.network_error;
  }
  
  // Check for timeout errors
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return {
      title: 'Request Timeout',
      message: 'The request took too long to complete.',
      action: 'Please try again.',
      severity: 'warning',
      canRetry: true,
      contactSupport: false,
    };
  }
  
  // Check for 500 errors
  if (error.response?.status >= 500) {
    return API_ERROR_CODES.server_error;
  }
  
  // Check for 429 errors
  if (error.response?.status === 429) {
    return API_ERROR_CODES.rate_limit;
  }
  
  // Check error message for known patterns
  return mapErrorMessageToCode(error.message || 'Unknown error');
}

// Map error messages to appropriate error codes
function mapErrorMessageToCode(message: string): PaymentError {
  const lowerMessage = message.toLowerCase();
  
  if (lowerMessage.includes('insufficient')) {
    return STRIPE_DECLINE_CODES.insufficient_funds;
  }
  if (lowerMessage.includes('expired')) {
    return STRIPE_DECLINE_CODES.expired_card;
  }
  if (lowerMessage.includes('incorrect') && lowerMessage.includes('number')) {
    return STRIPE_DECLINE_CODES.incorrect_number;
  }
  if (lowerMessage.includes('incorrect') && (lowerMessage.includes('cvc') || lowerMessage.includes('cvv'))) {
    return STRIPE_DECLINE_CODES.incorrect_cvc;
  }
  if (lowerMessage.includes('authentication')) {
    return STRIPE_DECLINE_CODES.authentication_required;
  }
  if (lowerMessage.includes('promotion code cannot be redeemed') && lowerMessage.includes('prior transactions')) {
    return API_ERROR_CODES.promotion_code_already_used;
  }
  if (lowerMessage.includes('promotion') && lowerMessage.includes('code')) {
    return API_ERROR_CODES.promotion_code_invalid;
  }
  if (lowerMessage.includes('subscription') && lowerMessage.includes('not found')) {
    return API_ERROR_CODES.subscription_not_found;
  }
  if (lowerMessage.includes('network') || lowerMessage.includes('connection')) {
    return API_ERROR_CODES.network_error;
  }
  if (lowerMessage.includes('cannot combine currencies') || lowerMessage.includes('currency mismatch')) {
    return API_ERROR_CODES.currency_mismatch;
  }
  
  return getGenericError();
}

// Get generic error for unknown cases
function getGenericError(): PaymentError {
  return {
    title: 'Payment Failed',
    message: 'We couldn\'t process your payment at this time.',
    action: 'Please try again or use a different payment method. If the problem persists, contact support.',
    severity: 'error',
    canRetry: true,
    contactSupport: true,
  };
}

// Format error for display
export function formatErrorForDisplay(error: any): {
  title: string;
  message: string;
  action: string;
  severity: 'error' | 'warning' | 'info';
  canRetry: boolean;
  contactSupport: boolean;
} {
  return extractErrorInfo(error);
}

// Check if error allows retry
export function canRetryPayment(error: any): boolean {
  const errorInfo = extractErrorInfo(error);
  return errorInfo.canRetry;
}

// Check if user should contact support
export function shouldContactSupport(error: any): boolean {
  const errorInfo = extractErrorInfo(error);
  return errorInfo.contactSupport;
} 